import { TFunction } from 'i18next';
import moment from 'moment';
import React, { ReactElement, ReactNode, useRef, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { ThemeContext } from 'styled-components';
import { useRouteMatch } from 'react-router-dom';

import { useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import useCurrentlyViewedUser from 'packages/kakapo-ui-application/hooks/useCurrentlyViewedUser';
import EvaluationIcon from 'packages/kakapo-ui-kit/assets/icons/EvaluationIcon';
import FeedbackIcon from 'packages/kakapo-ui-kit/assets/icons/FeedbackIcon';
import GoalIcon from 'packages/kakapo-ui-kit/assets/icons/GoalIcon';
import LearningIcon from 'packages/kakapo-ui-kit/assets/icons/LearningIcon';
import { SectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import JourneyListItem, {
	IEvaluationSubmissionV2,
	ILearningEvent,
	JourneyItem,
} from 'packages/kakapo-ui-kit/components/journey/JourneyListItem';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

interface IKakapoObject {
	createdOnDateTimeOffsetUtc: Date;
}

interface INodeObject {
	createdAt: Date;
}

const isKakapoObject = (item: IKakapoObject | INodeObject): item is IKakapoObject => {
	return (item as IKakapoObject).createdOnDateTimeOffsetUtc !== undefined;
};

export const createdTimestampFromJourneyItem = (item: JourneyItem): Date => {
	if (isKakapoObject(item)) {
		return item.createdOnDateTimeOffsetUtc;
	}
	return item.createdAt;
};
enum JourneyItemType {
	EvaluationForm,
	Goal,
	FeedbackEvent,
	EvaluationSubmission,
	LearningEvent,
}

const getJourneyItemType = (item: JourneyItem): JourneyItemType => {
	const match = item as any;

	if ((match as IEvaluationSubmissionV2).employee) {
		return JourneyItemType.EvaluationSubmission;
	}

	if ((match as ILearningEvent).learningType) {
		return JourneyItemType.LearningEvent;
	}

	return JourneyItemType.Goal;
};

const journeyItemFactory = {
	create({
		item,
		user,
		path,
		t,
		v2,
		localize,
	}: {
		item: JourneyItem;
		user: graphql.User;
		path: string;
		t: TFunction;
		v2: boolean;
		localize: (items: graphql.LocalizedString) => { text: string };
	}): ReactNode {
		if ('__typename' in item) {
			if (item.__typename === 'FeedbackRequest') {
				return (
					<JourneyListItem
						createdAt={item.createdAt}
						key={item.id}
						icon={<FeedbackIcon />}
						to={
							v2
								? `/users/${user.id}/feedback/${item.id}?return=/users/${user.id}/journey`
								: `/user/${user.id}/feedback-v2/${item.id}/results`
						}
						title={item.title || `${t('journey.list.item.label.feedbackEvent')}`}
						description={t('journey.item.decription.feedback', {
							timestamp: moment(item.createdAt).format('L'),
							resultsGiven: '',
						})}
					/>
				);
			}
		}

		switch (getJourneyItemType(item)) {
			case JourneyItemType.EvaluationSubmission: {
				return (
					<JourneyListItem
						createdAt={createdTimestampFromJourneyItem(item)}
						key={item.id}
						icon={<EvaluationIcon color="primary" />}
						to={
							v2
								? `/users/${user.id}/conversations/${item.id}?return=/users/${user.id}/journey`
								: `/user/${user.id}/evaluations-v2/${item.id}`
						}
						title={t('journey.list.item.label.evaluationForm')}
						description={
							localize(((item as any) as IEvaluationSubmissionV2).evaluationForm.title).text
						}
					/>
				);
			}

			case JourneyItemType.LearningEvent: {
				const learningEvent = item as ILearningEvent;
				return (
					<JourneyListItem
						createdAt={createdTimestampFromJourneyItem(item)}
						key={item.id}
						icon={<LearningIcon color="primary" />}
						to={
							v2
								? `/users/${user.id}/journey/learning/${item.id}`
								: `/user/${user.id}/journey/learning/${item.id}`
						}
						title={`${t('journey.list.item.label.learningEvent')}`}
						description={localize(learningEvent.title).text}
					/>
				);
			}

			default: {
				const goal = item as graphql.Goal;
				return (
					<JourneyListItem
						createdAt={createdTimestampFromJourneyItem(item)}
						key={item.id}
						icon={<GoalIcon />}
						to={
							v2
								? `/users/${user.id}/goals/${item.id}?return=/users/${user.id}/journey`
								: `/user/${user.id}/journey/goals/${item.id}`
						}
						title={t('journey.list.item.label.goal')}
						description={localize(goal.title).text}
						checklistItems={goal.checklistItems}
					/>
				);
			}
		}
	},
};

interface Props {
	month: string;
	items: JourneyItem[];
	nextMonthItemCount: number;
}

function drawSquigglyLine(
	canvas: HTMLCanvasElement,
	color: string,
	itemCount: number,
	nextItemCount: number,
) {
	const ctx = canvas.getContext('2d')!;
	ctx.fillStyle = color;
	ctx.strokeStyle = color;
	ctx.setLineDash([5, 4]);
	ctx.beginPath();

	const multiplier = itemCount % 3 === 0 ? 1 : (itemCount % 3) * 0.33;
	const multiplierNext = nextItemCount % 3 === 0 ? 1 : (nextItemCount % 3) * 0.33;

	const maxUsableWidth = multiplier * ctx.canvas.width;
	const maxUsableWidthNext = multiplierNext * ctx.canvas.width;
	ctx.moveTo(maxUsableWidth / 2, 0);

	ctx.bezierCurveTo(
		maxUsableWidth / 2 - 30,
		70,
		maxUsableWidthNext / 2 + 30,
		30,
		maxUsableWidthNext / 2,
		100,
	);
	ctx.stroke();
}

const Canvas = styled.canvas`
	width: 100%;
	height: 100px;
`;

function useV2() {
	const history = useHistory();
	return history.location.pathname.startsWith('/v2/');
}

export default function JourneyListMonth({
	month,
	items,
	nextMonthItemCount,
}: Props): ReactElement {
	const { t } = useTranslation();
	const { currentlyViewedUser } = useCurrentlyViewedUser();
	const ref = useRef<HTMLCanvasElement>(null);
	const { path } = useRouteMatch();

	const theme = useContext(ThemeContext);
	const v2 = useV2();

	useEffect(() => {
		if (nextMonthItemCount === 0) {
			return;
		}
		const canvas = ref.current;
		drawSquigglyLine(canvas!, theme.colors.slateBlue, items.length, nextMonthItemCount);
	}, []);

	const { localize } = useLocalize();

	return (
		<>
			<Box mb={2}>
				<SectionTitle>{moment(month, 'YYYY-MM').format('MMMM YYYY')}</SectionTitle>
			</Box>

			<Flex flexDirection="row" flexWrap="wrap" mx={-2} alignItems="stretch">
				{items.map((item) =>
					journeyItemFactory.create({
						user: currentlyViewedUser,
						item,
						t,
						v2,
						path,
						localize,
					}),
				)}
			</Flex>
			<Canvas width={800} height={100} ref={ref} />
		</>
	);
}
